import { FormInstance } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { UseMutateFunction } from 'react-query';
import { useSelector } from 'react-redux';
import {
  AggregateType,
  ChartFilter,
  CustomChartWidget,
  Widget,
} from 'api/dashboard/widget';
import { PageLoading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import {
  CustomChart,
  CustomChartFormValues,
} from 'components/organisms/dashboard/components/custom-chart';
import {
  fieldLabelSelector,
  setAggType,
  transformField,
} from 'components/organisms/dashboard/components/utils';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { excludedAllTasksFields } from 'components/organisms/tasks/constants';
import {
  FilterableField,
  GroupedOptions,
} from 'components/organisms/tasks/fields';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { ApiFilterTransformer } from 'components/pages/api-filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { RootState } from 'data';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import { EditWidgetMutationPayload, getQueryWidgetKey } from 'queries/widgets';
import { FieldsStore } from 'stores/fields';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';

interface EditWidgetProps {
  widget: CustomChartWidget;
  form: FormInstance<CustomChartFormValues>;
  groupedFields: GroupedOptions<FilterableField>[];
  defaultGroupedFields: GroupedOptions<FilterableField>[];
  transformer: ApiFilterTransformer<GroupedFilter>;
  closeDrawer: () => void;
  editWidget: UseMutateFunction<
    Widget,
    unknown,
    EditWidgetMutationPayload,
    unknown
  >;
}

export const EditCustomChartWidget: FC<EditWidgetProps> = ({
  widget,
  form,
  transformer,
  groupedFields,
  defaultGroupedFields,
  closeDrawer,
  editWidget,
}) => {
  const { data: projectCustomFields, isLoading: isProjectCustomFieldsLoading } =
    useProjectCustomFields();

  const fieldsStore = useSelector(
    ({ fields: taskFields }: RootState) => taskFields,
  );

  const taskCustomFields = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName, customField }) =>
          !excludedAllTasksFields.includes(fieldName) || !customField,
      ),
    [fieldsStore],
  );

  const translate = useTranslate();

  const [customChartsFilter, setCustomChartsFilter] = useState<ChartFilter>(
    () => {
      return widget.widgetObjectType === 'PROJECT'
        ? {
            projectsFilter: transformer.from(widget.simpleSearchFilter),
            tasksFilter: {
              type: QueryCombinerType.All,
              groupCriteria: [],
              defaultGroup: FilterGroupType.Project,
            },
          }
        : {
            projectsFilter: {
              type: QueryCombinerType.All,
              groupCriteria: [],
              defaultGroup: FilterGroupType.Project,
            },
            tasksFilter: transformer.from(widget.simpleSearchFilter),
          };
    },
  );
  const { metaData: widgetData } = widget;
  const updateWidget = (values: CustomChartFormValues) => {
    const { widgetName, objectType, aggType, chartType } = values;
    const filterOptions =
      objectType === 'PROJECT'
        ? customChartsFilter.projectsFilter
        : customChartsFilter.tasksFilter;

    editWidget(
      {
        widgetId: Number(widget.widgetId),
        simpleSearchFilter: transformer.to(filterOptions),
        widgetName,
        widgetObjectType: objectType,
        metaData: {
          aggType: setAggType(values.yAxis, aggType),
          chart: {
            chartType: chartType.value.toUpperCase(),
          },
          xAxis: transformField(values.xAxis),
          yAxis: transformField(values.yAxis),
          ...(values?.zoomLevel && {
            zoomLevel: values?.zoomLevel,
          }),
          ...(values?.groupBy && { groupBy: values?.groupBy?.value }),
        },
      },
      {
        onSuccess: async (res) => {
          await queryClient.invalidateQueries(
            getQueryWidgetKey(widget.widgetId),
          );
          await queryClient.refetchQueries({
            predicate: (query) =>
              query.queryKey[0] === 'resolve-multiple' &&
              query.queryKey[1] === widget.widgetId,
          });

          const message = translate('dashboard.chart.update.success.message', {
            widgetName: res.widgetName,
          });
          const title = translate('success');

          toastService({
            title,
            message,
          }).success();
          closeDrawer();
        },
      },
    );
  };

  const customFields =
    widget.widgetObjectType === 'PROJECT'
      ? projectCustomFields
      : taskCustomFields;

  return (
    <>
      {isProjectCustomFieldsLoading ? (
        <PageLoading />
      ) : (
        <CustomChart
          onFinish={updateWidget}
          defaultGroup={FilterGroupType.Project}
          groupedFields={groupedFields}
          defaultGroupedFields={defaultGroupedFields}
          transformer={transformer}
          form={form}
          filterFields={customChartsFilter}
          setFilter={setCustomChartsFilter}
          isEdit
          initialValues={{
            widgetName: widget.widgetName,
            objectType: widget.widgetObjectType,
            aggType: [AggregateType.Total, AggregateType.Count].includes(
              widgetData.aggType,
            )
              ? AggregateType.Total
              : AggregateType.Average,
            chartType: {
              label:
                widgetData.chart.chartType.toLowerCase() === 'column'
                  ? 'Column'
                  : 'Line',
              value: widgetData.chart.chartType.toLowerCase(),
            },
            xAxis: {
              label: widgetData.xAxis.title,
              value: widgetData.xAxis.field,
            },
            yAxis: {
              label: widgetData.yAxis.title,
              value: widgetData.yAxis.field,
            },
            ...(widgetData?.groupBy && {
              groupBy: {
                label: translate(
                  fieldLabelSelector(widgetData?.groupBy, customFields),
                ),
                value: widgetData.groupBy,
              },
            }),
            ...(widgetData.zoomLevel && {
              zoomLevel: widgetData.zoomLevel,
            }),
          }}
        />
      )}
    </>
  );
};
